import React from 'react';
import { ampli } from '@/ampli';
import SettingsNotificationsButton from '@/components/Notifications/SettingsNotificationButton';
import log from '@/lib/logger';
import { rootRoute } from '@/modules/core/root/route';
import OnboardingProvider from '@/providers/OnboardingContext';
import { Button } from '@/shared/components/ui/button';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/shared/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { createRoute, Outlet, redirect } from '@tanstack/react-router';
import { Fullscreen, Shrink } from 'lucide-react';

import { AppSidebar } from '../components/app-sidebar';

// * This is a pathless route
export const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: ProtectedLayout,
  beforeLoad: async ({ context }) => {
    await context.auth.checkAuth();

    if (!context.auth.isAuthenticated()) {
      throw redirect({
        to: '/login',
      });
    }
  },
});

// ! Reminder to redo this after the migration is complete
function ProtectedLayout() {
  const [isFullscreen, setFullscreen] = React.useState(!!document.fullscreenElement);

  const openFullscreen = async () => {
    ampli.fullScreenModeClicked();
    const element = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
      msRequestFullscreen?: () => Promise<void>;
    };

    if (element.requestFullscreen) {
      try {
        await element.requestFullscreen();
      } catch (e) {
        log.error(e);
      }
    } else if (element.webkitRequestFullscreen) {
      // * Safari
      await element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) {
      // * IE11
      await element.msRequestFullscreen();
    }
  };

  const closeFullscreen = async () => {
    const document = window.document as Document & {
      exitFullscreen: () => Promise<void>;
      webkitExitFullscreen: () => Promise<void>;
      msExitFullscreen: () => Promise<void>;
    };

    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      // * Safari
      await document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      // * IE11
      await document.msExitFullscreen();
    }
  };

  React.useEffect(() => {
    const toggleFullscreen = () => {
      setFullscreen(!!document.fullscreenElement);
    };
    document.documentElement.addEventListener('fullscreenchange', toggleFullscreen);

    return () => {
      document.documentElement.removeEventListener('fullscreenchange', toggleFullscreen);
    };
  }, []);

  return (
    <SidebarProvider>
      <AppSidebar />
      <OnboardingProvider>
        <SidebarInset
          className="$tw-flex $tw-h-full $tw-w-full $tw-flex-col $tw-overflow-hidden $tw-text-foreground"
          style={{
            backgroundImage: `
        linear-gradient(to right, #D3D3D30D 1px, transparent 1px),
        linear-gradient(to bottom, #D3D3D30D 1px, transparent 1px)
      `,
            backgroundSize: '48px 48px',
          }}
        >
          <header
            className="$tw-flex $tw-h-24 $tw-w-full $tw-shrink-0 $tw-items-center $tw-justify-between $tw-gap-2 $tw-bg-background-2 $tw-px-4 $tw-py-4 $tw-shadow-sm $tw-transition-[width,height] $tw-ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:$tw-h-20"
            style={{
              backgroundImage: `
        linear-gradient(to right, #D3D3D31A 1px, transparent 1px),
        linear-gradient(to bottom, #D3D3D31A 1px, transparent 1px)
      `,
              backgroundSize: '48px 48px',
            }}
          >
            <SidebarTrigger />
            <div id="user-profile-options" className="$tw-flex $tw-items-center $tw-gap-2">
              <SettingsNotificationsButton />
              <TooltipProvider>
                {isFullscreen ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={closeFullscreen}>
                        <Shrink />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>Salir de pantalla completa</span>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={openFullscreen}
                        autoFocus={false}
                      >
                        <Fullscreen />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>Pantalla completa</span>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          </header>
          <main className="$tw-flex-1 $tw-overflow-y-auto $tw-p-4">
            <Outlet />
          </main>
        </SidebarInset>
      </OnboardingProvider>
    </SidebarProvider>
  );
}
